import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Scales, ArrowRight, Scroll } from "@phosphor-icons/react";
import { IS_FARMERSPULSE, SITE_META } from "@/lib/site";
import SEOHead from "@/components/SEOHead";

/**
 * Public index for /legal — lists every published `page_type=legal`
 * CMS document. The full body is fetched on the per-slug page below.
 */
export default function LegalIndex() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    http.get("/cms/pages?page_type=legal&limit=100")
      .then(({ data }) => { if (!cancelled) setPages(data.pages || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.title = `Legal · ${SITE_META.brand}`;
  }, []);

  return (
    <main
      data-testid="legal-index"
      className="min-h-screen pt-24 md:pt-28 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#241209]"
    >
      <SEOHead
        title="Legal Notices — Terms, Privacy, Policies | FarmersPulse™"
        description="Terms of Service, Privacy Policy, Community Guidelines, Agricultural Disclaimer, Food Safety Policy and other legal documents that govern how FarmersPulse operates."
        path="/legal"
      />
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
          <Scales size={12} weight="fill" /> {SITE_META.brand} · Legal
        </div>
        <h1 className="mt-3 font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter text-[#151312]">
          Legal notices.
        </h1>
        <p className="mt-5 text-lg text-[#3A3830] max-w-2xl">
          The terms, policies and disclaimers that govern how {SITE_META.brand} operates — for farmers, growers, communities, marketplace users, and the broader SocialPulse ecosystem.
        </p>

        <div className="mt-12 space-y-3" data-testid="legal-index-list">
          {loading ? (
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#8E8B7F]">Loading…</div>
          ) : pages.length === 0 ? (
            <div className="rounded-2xl border border-black/10 bg-white/60 p-6 text-[#3A3830]">
              Legal documents are being finalized. Please check back shortly.
            </div>
          ) : pages.map((p) => (
            <Link
              key={p.slug}
              to={IS_FARMERSPULSE ? `/legal/${p.slug}` : `/farmers/legal/${p.slug}`}
              data-testid={`legal-index-item-${p.slug}`}
              className="group block rounded-2xl border border-black/10 bg-white/70 p-5 md:p-6 hover:border-[color:var(--np-brand,#B85F2A)] hover:bg-white transition-colors"
            >
              <div className="flex items-start gap-3">
                <Scroll size={18} weight="regular" className="mt-1 text-[color:var(--np-brand,#B85F2A)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tight text-[#151312] group-hover:text-[color:var(--np-brand,#B85F2A)]">
                    {p.title}
                  </div>
                  {p.subtitle && (
                    <div className="mt-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8E8B7F]">
                      {p.subtitle}
                    </div>
                  )}
                  {p.excerpt && (
                    <p className="mt-2 text-sm text-[#3A3830] leading-relaxed">{p.excerpt}</p>
                  )}
                </div>
                <ArrowRight size={16} weight="bold" className="mt-1 text-[#3A3830] group-hover:text-[color:var(--np-brand,#B85F2A)] flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
